'use client';

import React from 'react';
import SocialLinks from './SocialLinks';
import { BackgroundBeams } from './ui/background-beams';
import { asText, Content } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import type { IntegrationDocument } from '@/prismicio-types';
import Image from 'next/image';
import { PrismicNextImage } from '@prismicio/next';
import { useLanguage } from './LanguageProvider';
import Link from 'next/link';
import TransitionLink from './TransitionLink';
import { CurvedDivider } from './CurvedDivider';
import { MailIcon, Phone } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { phoneHref } from '@/lib/siteSettings';

interface FooterContact {
  email: string;
  telephone: string;
  whatsappUrl: string;
}

interface FooterProps {
  data: Content.FooterDocument['data'];
  integrations?: IntegrationDocument[];
  viewAllButton?: string;
  totalCount?: number;
  contact: FooterContact;
  /** Server-computed copyright year — reading the clock here would mismatch SSR HTML at year boundaries (React #418). */
  year: number;
}

function formatIntegrationTitle(integration: IntegrationDocument): string {
  const raw = asText(integration.data.hero_title);
  const stripped = raw
    .replace(/\s*\+\s*NetSuite\s+整合方案\s*$/i, '')
    .replace(/\s*\+\s*NetSuite\s+整合\s*$/i, '')
    .replace(/\s*NetSuite\s+整合方案\s*$/i, '')
    .replace(/\s*NetSuite\s+整合\s*$/i, '')
    .replace(/\s*\+\s*NetSuite\s+Integration\s*$/i, '')
    .replace(/\s*NetSuite\s+Integration\s*$/i, '')
    .replace(/^NetSuite\s*\+\s*/i, '')
    .trim();
  return `NetSuite + ${stripped}`;
}

const Footer: React.FC<FooterProps> = ({ data, integrations, viewAllButton, totalCount, contact, year }) => {
  const { currentLang } = useLanguage();
  return (
    <>
      <footer className="relative bg-[#111] sm:bg-gradient-to-b sm:from-[#222] sm:to-[#000]">
        <CurvedDivider top={true} color="#fff" />
        <div className="container relative z-10 mx-auto py-16 md:py-16 md:pt-28">
          <div className="flex flex-wrap justify-between gap-6 sm:gap-0">
            {/* Left Column */}
            <div className="mt-6 w-full sm:pb-16 sm:pr-[3rem] md:mb-0 md:mt-0 xl:w-auto xl:[width:30%]">
              {data.footer_logo?.url ? (
                <PrismicNextImage
                  field={data.footer_logo}
                  className="footer-logo mb-2 h-auto w-auto max-w-[150px]"
                />
              ) : (
                <div className="footer-logo mb-2 text-2xl font-normal text-gray-200 sm:text-3xl">
                  <span className="footer-one bg-gradient-to-b from-red-400 to-red-700 bg-clip-text text-transparent">
                    <span className="footer-cap">O</span>ne
                  </span>
                  Pacific
                </div>
              )}
              {Array.isArray(data.company_blurb) ? (
                <div className="mt-4 max-w-xl text-gray-400 [&_a:hover]:text-gray-200 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:text-gray-200">
                  <PrismicRichText field={data.company_blurb} />
                </div>
              ) : (
                <p className="mt-4 max-w-xl text-gray-400">
                  {data.company_blurb as unknown as string}
                </p>
              )}
              <div className="mt-4 flex items-center gap-6 lg:flex-col lg:items-start lg:gap-4 xl:mt-12">
                <Image
                  className="w-[140px] rounded-[10px] border-gray-500 p-[10px_12px_14px_0px] grayscale sm:w-[200px]"
                  src="/netsuite-erp-award-laurel-600.png"
                  width={940}
                  height={350}
                  alt="OnePac Netsuite ERP Excellence Award"
                />
                <Image
                  className="w-[140px] p-[10px_12px_14px_0px] grayscale sm:w-[200px]"
                  src="/award2.png"
                  width={600}
                  height={211}
                  alt="OnePac NetSuite Award"
                />
              </div>
            </div>

            {/* Contact Column */}
            <div className="md:justify-left mt-16 flex w-full border-t border-[rgba(255,255,255,0.05)] pl-0 pt-4 md:mt-0 md:w-1/3 md:border-t-0 md:pt-0 xl:w-auto xl:border-l xl:pl-4 xl:[width:20%]">
              <div className="w-full max-w-[400px] pr-6">
                <h3 className="mb-10 text-sm font-semibold uppercase text-gray-200">
                  {data.contact_title}
                </h3>
                <p
                  className="text-gray-400"
                  dangerouslySetInnerHTML={{
                    __html: data.contact_address
                      ? data.contact_address.replace(/\n/g, '<br />')
                      : '',
                  }}
                />
                <div className="mt-6">
                  <MailIcon
                    className="mr-2 inline-block h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <a
                    className="text-blue-400 transition-colors hover:text-blue-500"
                    href={`mailto:${contact.email}?subject=One Pacific Inquiry`}
                    aria-label={`Email us at ${contact.email}`}
                  >
                    {contact.email}
                  </a>
                  <div className="mt-3">
                    <Phone
                      className="mr-2 inline-block h-5 w-5 rotate-12 text-gray-400"
                      aria-hidden="true"
                    />
                    <a
                      className="inline-block text-blue-400 transition-colors hover:text-blue-500"
                      href={phoneHref(contact.telephone)}
                      aria-label={`Call us at ${contact.telephone}`}
                      data-location="Footer"
                    >
                      <span className="inline">{contact.telephone}</span>
                    </a>
                  </div>
                  <div className="mt-3">
                    <FaWhatsapp
                      className="mr-2 inline-block h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                    <a
                      href={contact.whatsappUrl}
                      className="inline-block text-blue-400 transition-colors hover:text-blue-500"
                      target="_blank"
                      data-location="Footer"
                      rel="nofollow noopener"
                    >
                      <span>WhatsApp</span>
                    </a>
                  </div>
                </div>
                <div className="mt-12">
                  <SocialLinks />
                </div>

                <h3 className="mb-10 mt-20 text-sm font-semibold uppercase text-gray-200">
                  {data.services_title}
                </h3>
                <ul className="list-none">
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/implementations`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title={`NetSuite ${data.implementations_link_title}`}
                    >
                      {data.implementations_link_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/integrations`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title={`NetSuite ${data.integrations_link_title}`}
                    >
                      {data.integrations_link_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/consulting`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title={`NetSuite ${data.consulting_link_title}`}
                    >
                      {data.consulting_link_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/managed-services`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title={`NetSuite ${data.managed_services_link_title}`}
                    >
                      {data.managed_services_link_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/partner-support`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title={`NetSuite ${data.partner_support_link_title}`}
                    >
                      {data.partner_support_link_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/netsuite-rescue`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title={`${data.netsuite_rescue_link_title || 'Rescue Service'}`}
                    >
                      {data.netsuite_rescue_link_title || 'Rescue Service'}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/health-check`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title={`NetSuite ${data.health_check_link_title}`}
                    >
                      {data.health_check_link_title}
                    </TransitionLink>
                  </li>
                </ul>
              </div>
            </div>

            {/* Integrations & Industries Column */}
            <div className="md:justify-left mt-16 flex w-full border-t border-[rgba(255,255,255,0.05)] pl-0 pt-4 md:mt-0 md:w-1/3 md:border-l md:border-t-0 md:pl-4 md:pt-0 xl:w-auto xl:[width:20%]">
              <div className="w-full max-w-[400px] pr-4">
                {integrations && integrations.length > 0 && (data as any).netsuite_integrations_title && (
                  <>
                    <h3 className="mb-10 text-sm font-semibold uppercase text-gray-200">
                      {(data as any).netsuite_integrations_title}
                    </h3>
                    <ul className="list-none">
                      {integrations.map((integration) => (
                        <li key={integration.uid} className="mb-2">
                          <TransitionLink
                            href={`/${currentLang}/integrations/${integration.uid}`}
                            className="text-blue-400 transition-colors hover:text-blue-500"
                          >
                            {formatIntegrationTitle(integration)}
                          </TransitionLink>
                        </li>
                      ))}
                    </ul>
                    {viewAllButton != null && totalCount != null && (
                      <div className="mt-6">
                        <TransitionLink
                          href={`/${currentLang}/integrations#matrix`}
                          className="group flex items-center gap-1 text-blue-400 transition-colors hover:text-blue-500"
                        >
                          {viewAllButton.replace('#', String(totalCount))}
                          <span className="transition-transform duration-150 group-hover:translate-x-0.5">&rsaquo;</span>
                        </TransitionLink>
                      </div>
                    )}
                  </>
                )}

                {/* NetSuite Industries Section */}
                <h3
                  className="mb-10 mt-20 text-sm font-semibold uppercase text-gray-200"
                  title={`NetSuite Industries We Service`}
                >
                  {data.industries_title}
                </h3>
                <ul className="list-none">
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.construction_title}`}
                      href={`/${currentLang}/industries/construction`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.construction_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.energy_title}`}
                      href={`/${currentLang}/industries/energy`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.energy_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.finance_title}`}
                      href={`/${currentLang}/industries/finance`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.finance_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.manufacturing_title}`}
                      href={`/${currentLang}/industries/manufacturing`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.manufacturing_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.professional_title}`}
                      href={`/${currentLang}/industries/professional`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.professional_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.retail_title}`}
                      href={`/${currentLang}/industries/retail`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.retail_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.science_title}`}
                      href={`/${currentLang}/industries/science`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.science_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.software_title}`}
                      href={`/${currentLang}/industries/software`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.software_title}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      title={`NetSuite for ${data.wholesale_title}`}
                      href={`/${currentLang}/industries/wholesale`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                    >
                      {data.wholesale_title}
                    </TransitionLink>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Column */}
            <div className="mb-6 mt-16 w-full border-t border-[rgba(255,255,255,0.05)] pl-0 pt-4 text-left md:mb-0 md:mt-0 md:w-1/3 md:border-l md:border-t-0 md:pl-4 md:pt-0 md:text-left xl:w-auto xl:[width:20%]">
              <h3 className="mb-10 text-sm font-semibold uppercase text-gray-200">
                {data.company_title}
              </h3>
              <ul className="list-none">
                <li className="mb-2">
                  <TransitionLink
                    href={`/${currentLang}/`}
                    className="text-blue-400 transition-colors hover:text-blue-500"
                  >
                    {data.home_link_title}
                  </TransitionLink>
                </li>
                <li className="mb-2">
                  <TransitionLink
                    href={`/${currentLang}/about`}
                    className="text-blue-400 transition-colors hover:text-blue-500"
                  >
                    {data.about_link_title}
                  </TransitionLink>
                </li>
                <li className="mb-2">
                  <TransitionLink
                    href={`/${currentLang}/success`}
                    className="text-blue-400 transition-colors hover:text-blue-500"
                  >
                    {data.success_stories_link_title}
                  </TransitionLink>
                </li>
                <li className="mb-2">
                  <TransitionLink
                    href={`/${currentLang}/blog`}
                    className="text-blue-400 transition-colors hover:text-blue-500"
                  >
                    {data.blog_title}
                  </TransitionLink>
                </li>
                <li className="mb-2">
                  <TransitionLink
                    href={`/${currentLang}/videos`}
                    className="text-blue-400 transition-colors hover:text-blue-500"
                  >
                    {data.netsuite_videos}
                  </TransitionLink>
                </li>
                <li className="mb-2">
                  <TransitionLink
                    href={`/${currentLang}/events`}
                    className="text-blue-400 transition-colors hover:text-blue-500"
                  >
                    {data.netsuite_events}
                  </TransitionLink>
                </li>
                <li className="mb-2">
                  <TransitionLink
                    href={`/${currentLang}/faq`}
                    className="text-blue-400 transition-colors hover:text-blue-500"
                  >
                    {data.faq_link_title}
                  </TransitionLink>
                </li>
                <li className="mb-2">
                  <TransitionLink
                    href={`/${currentLang}/contact`}
                    data-location="Footer"
                    className="ga-contact text-blue-400 transition-colors hover:text-blue-500"
                  >
                    {data.contact_us_link_title}
                  </TransitionLink>
                </li>
              </ul>
              <div>
                <h3 className="mb-10 mt-20 text-sm font-semibold uppercase text-gray-200">
                  {data.our_offices_label}
                </h3>
                <ul className="list-none">
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/contact#locations`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title="Hong Kong"
                    >
                      {data.offices_hong_kong}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/contact#locations`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title="Taiwan"
                    >
                      {data.offices_taiwan}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/contact#locations`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title="Malaysia"
                    >
                      {data.offices_malaysia}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/contact#locations`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title="United States"
                    >
                      {data.offices_united_states}
                    </TransitionLink>
                  </li>
                  <li className="mb-2">
                    <TransitionLink
                      href={`/${currentLang}/contact#locations`}
                      className="text-blue-400 transition-colors hover:text-blue-500"
                      title="India"
                    >
                      {data.offices_india}
                    </TransitionLink>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="relative z-20 bg-[#000]">
          <div className="container mx-auto flex h-auto flex-col items-center justify-between space-y-2 py-4 sm:h-[50px] sm:flex-row sm:space-y-0">
            <div className="w-full text-center text-sm text-gray-400 sm:w-auto sm:text-left">
              &copy; {year} <a className="underline" href="https://psglobalconsulting.com/">PS Global Consulting</a>. {data.all_rights_reserved}
            </div>
            <ul className="m-0 flex w-full list-none justify-center space-x-2 p-0 text-center text-sm sm:w-auto sm:justify-start sm:text-left">
              <li>
                <TransitionLink
                  href={`/${currentLang}/terms`}
                  className="text-gray-400 transition-colors hover:text-gray-300"
                >
                  {data.terms_link_title}
                </TransitionLink>
              </li>
              <li aria-hidden="true" className="text-gray-400 opacity-50">
                |
              </li>
              <li>
                <TransitionLink
                  href={`/${currentLang}/privacy`}
                  className="text-gray-400 transition-colors hover:text-gray-300"
                >
                  {data.privacy_link_title}
                </TransitionLink>
              </li>
              <li aria-hidden="true" className="text-gray-400 opacity-50">
                |
              </li>
              <li>
                <Link
                  href="https://ryanmac.com"
                  className="text-gray-400 transition-colors hover:text-gray-300"
                >
                  {data.site_by_label} <b>RyanMac</b>
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <BackgroundBeams opacity={0.025} />
      </footer>
    </>
  );
};

export default Footer;
